import React, { useEffect, useRef, useState } from 'react';
import { IconLogout } from '@tabler/icons-react';
import axios from '../../library/axios';
import {
  getCpanelAssetUrl,
  formatCentralRoleLabel,
  getCentralUser,
  getCentralUserInitials,
  setCentralUser,
} from '../../helpers/cpanelAuth';
import CpanelNotifications from './CpanelNotifications';

const CentralUserAvatar = ({ user, displayName, size = 'md', className = '' }) => {
  const pictureUrl = getCpanelAssetUrl(user?.picture);
  const initials = getCentralUserInitials(user);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [pictureUrl]);

  const showImage = pictureUrl && !imageFailed;

  const sizeClass = size === 'lg' ? 'central-user-avatar--lg' : 'central-user-avatar--sm';

  return (
    <div className={`central-user-avatar ${sizeClass} ${className}`.trim()}>
      {showImage ? (
        <img
          src={pictureUrl}
          alt={displayName}
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className="central-user-avatar__initials">{initials}</span>
      )}
    </div>
  );
};

const CpanelHeader = ({ onLogout }) => {
  const menuRef = useRef(null);
  const [user, setUser] = useState(() => getCentralUser());
  const displayName = user?.namaLengkap || user?.username || 'Administrator';
  const username = user?.username || 'admin';
  const roleLabel = formatCentralRoleLabel(user?.role) || 'Operator';

  useEffect(() => {
    if (user?.role) return undefined;

    let cancelled = false;
    axios
      .get('/cpanel/auth/me')
      .then(({ data }) => {
        if (cancelled || !data?.user) return;
        const merged = { ...getCentralUser(), ...data.user };
        setCentralUser(merged);
        setUser(merged);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [user?.role]);

  useEffect(() => {
    const initMenu = () => {
      if (typeof window.KTMenu === 'undefined') return;
      window.KTMenu.createInstances();
      const menuEl = menuRef.current?.querySelector('[data-kt-menu="true"]');
      if (menuEl && typeof window.KTMenu.getInstance === 'function') {
        const instance = window.KTMenu.getInstance(menuEl);
        instance?.update?.();
      }
    };

    initMenu();
    const timer = window.setTimeout(initMenu, 0);
    return () => window.clearTimeout(timer);
  }, [displayName, user?.picture]);

  const handleLogoutClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onLogout?.();
  };

  return (
    <div
      className="d-flex align-items-center justify-content-end flex-lg-grow-1 gap-2 cpanel-header-actions"
    >
      <CpanelNotifications />
      <div
        ref={menuRef}
        className="cpanel-header-user"
        id="kt_header_user_menu_toggle"
      >
      <div
        className="cpanel-header-user__toggle"
        data-kt-menu-trigger="click"
        data-kt-menu-attach="parent"
        data-kt-menu-placement="bottom-end"
      >
        <CentralUserAvatar user={user} displayName={displayName} size="sm" />
        <span className="cpanel-header-user__name d-none d-sm-inline">{displayName}</span>
        <span className="cpanel-header-user__chevron" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width={14} height={14} viewBox="0 0 24 24" fill="none">
            <path
              d="M6 9L12 15L18 9"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>

      <div
        className="menu menu-sub menu-sub-dropdown central-user-menu"
        data-kt-menu="true"
      >
        <div className="central-user-menu__header">
          <CentralUserAvatar user={user} displayName={displayName} size="lg" />
          <div className="central-user-menu__info">
            <span className="central-user-menu__name">{displayName}</span>
            <span className="central-user-menu__username">@{username}</span>
            <span className="central-user-menu__badge">{roleLabel}</span>
          </div>
        </div>

        <div className="central-user-menu__body">
          <button type="button" className="central-user-menu__logout" onClick={handleLogoutClick}>
            <span className="central-user-menu__logout-icon">
              <IconLogout size={18} stroke={1.75} />
            </span>
            <span>Keluar</span>
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

export default CpanelHeader;
